import { PencilSimple, Trash } from 'phosphor-react';
import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { ScheduledPayment, formatMoney } from '@dill/dill-shared';
import { DillButton, DillModal } from '../../../general/Dill';
import AppDatePicker from '../../../general/AppDatePicker/AppDatePicker';
import { ScheduledPaymentsSection } from '../../../general/ScheduledPaymentsSection/ScheduledPaymentsSection';
import {
  formatScheduledDate,
  toLocalCalendarDay,
} from '../../../general/ScheduledPaymentsSection/scheduledPaymentDisplay';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { useParamMainBuyerId } from '../../../../utils/hooks/useParamMainBuyerId';
import {
  earliestSchedulableDate,
  earliestSchedulableDateMessage,
  isBeforeEarliestSchedulableDate,
  openDillToast,
} from '../../../../utils/helpers';
import {
  cancelScheduledPayment,
  getScheduledPayments,
  updateScheduledPaymentDate,
} from '../../../../redux/services/paymentsService';
import { openMessageModal } from '../../../../redux/globalSlices/genericSlice';

/**
 * Upcoming one-off scheduled payments for the current main buyer, with reschedule and cancel.
 *
 * Renders nothing when there are none, so the Payments page is unchanged for buyers who have never
 * scheduled a payment.
 */
export const BuyerScheduledPayments = () => {
  const dispatch = useAppDispatch();
  const [selectedMainBuyerId] = useParamMainBuyerId();
  const { scheduledPayments, loadingList } = useAppSelector((state) => state.payments);
  const { selectedBuyerSuppliers } = useAppSelector((state) => state.buyers);

  const [editing, setEditing] = useState<ScheduledPayment | null>(null);
  const [editedDate, setEditedDate] = useState<Date | null>(null);
  const [editErrorMessage, setEditErrorMessage] = useState('');

  useEffect(() => {
    if (selectedMainBuyerId) {
      dispatch(getScheduledPayments({ mainBuyerId: selectedMainBuyerId }));
    }
  }, [selectedMainBuyerId]);

  const supplierNameById = useMemo(() => {
    const result = new Map<string, string>();
    for (const supplierForBuyer of selectedBuyerSuppliers) {
      if (supplierForBuyer.id) {
        result.set(supplierForBuyer.id, supplierForBuyer.name ?? '');
      }
    }
    return result;
  }, [selectedBuyerSuppliers]);

  const minimumDate = useMemo(() => earliestSchedulableDate(), [editing]);

  const handleOpenEdit = (scheduledPayment: ScheduledPayment) => {
    setEditing(scheduledPayment);
    setEditedDate(toLocalCalendarDay(scheduledPayment.scheduledDate));
    setEditErrorMessage('');
  };

  /**
   * Applies the new date, optionally carrying the buyer's acceptance of a changed total.
   *
   * Split out from `handleSaveDate` so the confirm-and-retry path re-uses exactly the same call
   * rather than a near-copy that could drift from it.
   */
  const submitDateChange = async (
    scheduledPaymentId: string,
    newDate: Date,
    confirmedTotalAmount?: number
  ) => {
    if (!selectedMainBuyerId) {
      return;
    }
    const result = await dispatch(
      updateScheduledPaymentDate({
        mainBuyerId: selectedMainBuyerId,
        scheduledPaymentId,
        scheduledDate: moment(newDate).format('YYYY-MM-DD'),
        confirmedTotalAmount,
      })
    );

    if (result.type !== 'payments/updateScheduledPaymentDate/fulfilled') {
      setEditErrorMessage((result.payload as string) || 'Failed to update scheduled payment');
      return;
    }

    const payload = result.payload as {
      success: boolean;
      requiresTotalConfirmation?: boolean;
      previousTotalAmount?: number;
      newTotalAmount?: number;
      message?: string;
    } | null;

    // The move would change what they pay. Ask before doing it — the run-date job reprices either
    // way, so the only choice here is whether the buyer hears about it now or on the day.
    if (payload?.requiresTotalConfirmation) {
      const previousTotal = payload.previousTotalAmount ?? 0;
      const newTotal = payload.newTotalAmount ?? 0;
      const isIncrease = newTotal > previousTotal;
      setEditing(null);
      dispatch(
        openMessageModal({
          buttonText: 'Keep current date',
          isSecondButton: true,
          secondButtonText: isIncrease ? 'Move it anyway' : 'Move it',
          modalType: 'WARN',
          title: isIncrease ? 'This date costs more' : 'This date changes the amount',
          subTitle: `${payload.message ?? ''} Moving to ${moment(newDate).format(
            'MM/DD/YYYY'
          )} changes this payment from ${formatMoney(previousTotal, 2)} to ${formatMoney(
            newTotal,
            2
          )}.`,
          onSecondButtonClick: () => {
            submitDateChange(scheduledPaymentId, newDate, newTotal);
          },
        })
      );
      return;
    }

    openDillToast({ message: 'Scheduled payment updated', type: 'SUCCESS' });
    setEditing(null);
    dispatch(getScheduledPayments({ mainBuyerId: selectedMainBuyerId }));
  };

  const handleSaveDate = async () => {
    if (!selectedMainBuyerId || !editing || !editedDate) {
      return;
    }
    if (isBeforeEarliestSchedulableDate(editedDate)) {
      setEditErrorMessage(earliestSchedulableDateMessage());
      return;
    }
    await submitDateChange(editing.id, editedDate);
  };

  const performCancel = async (scheduledPayment: ScheduledPayment) => {
    if (!selectedMainBuyerId) {
      return;
    }
    const result = await dispatch(
      cancelScheduledPayment({
        mainBuyerId: selectedMainBuyerId,
        scheduledPaymentId: scheduledPayment.id,
      })
    );
    if (result.type === 'payments/cancelScheduledPayment/fulfilled') {
      openDillToast({ message: 'Scheduled payment cancelled', type: 'SUCCESS' });
    } else {
      openDillToast({
        message: (result.payload as string) || 'Failed to cancel scheduled payment',
        type: 'ERROR',
      });
    }
  };

  const handleCancel = (scheduledPayment: ScheduledPayment) => {
    if (!selectedMainBuyerId) {
      return;
    }
    const supplierName = supplierNameById.get(scheduledPayment.supplierId) ?? 'this supplier';
    dispatch(
      openMessageModal({
        // The dismiss button is worded away from "Cancel" on purpose: next to "cancel this payment"
        // a button labelled Cancel is ambiguous about which thing it cancels.
        buttonText: 'Keep payment',
        isSecondButton: true,
        secondButtonText: 'Cancel payment',
        modalType: 'WARN',
        title: 'Cancel this scheduled payment?',
        subTitle: `The ${formatMoney(
          scheduledPayment.totalAmount,
          2
        )} payment to ${supplierName} scheduled for ${formatScheduledDate(
          scheduledPayment.scheduledDate,
          'MM/DD/YYYY'
        )} will not be sent. You can schedule it again at any time.`,
        onSecondButtonClick: () => {
          performCancel(scheduledPayment);
        },
      })
    );
  };

  const isUpdating =
    loadingList.includes('updateScheduledPaymentDate') ||
    loadingList.includes('cancelScheduledPayment');

  const supplierNameFor = (scheduledPayment: ScheduledPayment) =>
    supplierNameById.get(scheduledPayment.supplierId) ?? scheduledPayment.supplierId;

  return (
    <>
      <ScheduledPaymentsSection
        title="Upcoming Scheduled Payments"
        description="Payments you have queued for a future date. Expand one to see the invoices it covers."
        scheduledPayments={scheduledPayments}
        isLoading={loadingList.includes('getScheduledPayments')}
        counterpartyLabel="Supplier"
        counterpartyNameFor={supplierNameFor}
        renderActions={(scheduledPayment) => (
          <>
            <DillButton
              variant="icon-blue"
              icon={<PencilSimple size={16} />}
              onClick={() => handleOpenEdit(scheduledPayment)}
              isDisabled={scheduledPayment.status !== 'SCHEDULED' || isUpdating}
            />
            <DillButton
              variant="icon-red"
              icon={<Trash size={16} />}
              onClick={() => handleCancel(scheduledPayment)}
              isDisabled={scheduledPayment.status !== 'SCHEDULED' || isUpdating}
            />
          </>
        )}
      />

      <DillModal
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title="Reschedule Payment"
        subtitle={
          editing ? supplierNameById.get(editing.supplierId) ?? editing.supplierId : undefined
        }
        maxWidth="420px"
        contentClassName="px-5 py-5 flex flex-col gap-3"
        footer={
          <>
            <DillButton
              label="Cancel"
              variant="secondary"
              onClick={() => setEditing(null)}
              isDisabled={isUpdating}
            />
            <DillButton
              label="Save date"
              variant="primary"
              onClick={handleSaveDate}
              isLoading={isUpdating}
            />
          </>
        }>
        <AppDatePicker
          selectedDate={editedDate}
          onDateSelected={(date) => {
            setEditedDate(date ?? null);
            setEditErrorMessage('');
          }}
          minumumDate={minimumDate}
          dateFormat="MM/dd/yyyy"
          label="New payment date"
        />
        {editErrorMessage && <p className="text-xs text-red-500 m-0">{editErrorMessage}</p>}
      </DillModal>
    </>
  );
};

export default BuyerScheduledPayments;
